import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const CreateLobbyForm: React.FC = () => {
  const [lobbyData, setLobbyData] = useState({
    name: '',
    description: '',
    maxPlayers: 16
  });
  const [createdLobby, setCreatedLobby] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [showMatchModal, setShowMatchModal] = useState(false);

  const fetchPlayers = async (lobbyId: string) => {
    if (!lobbyId) return;
    try {
      const { data: playersData, error } = await supabase
        .from('players')
        .select('*')
        .eq('lobby_id', lobbyId)
        .order('joined_at', { ascending: true });

      if (error) {
        console.error('Error fetching players:', error);
      } else if (playersData) {
        setPlayers(playersData);
      }
    } catch (err) {
      console.error('Unexpected error fetching players:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    try {
      // Determine a valid UUID for created_by. Use authenticated user id if available,
      // otherwise fall back to a temporary UUID (browser-supported crypto.randomUUID).
      let createdBy: string | null = null;
      try {
        // supabase.auth.getUser() is async and returns the logged-in user when auth is set up
        // If auth isn't configured or there's no user, this will safely fall through.
        const res = await supabase.auth.getUser();
        if (res?.data?.user?.id) {
          createdBy = res.data.user.id;
        }
      } catch (authErr) {
        // ignore; we'll fallback to random UUID
      }

      if (!createdBy) {
        if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
          createdBy = (crypto as any).randomUUID();
          console.info('[CreateLobby] no auth user — using temporary created_by:', createdBy);
        } else {
          // As a last resort, use a short timestamp-based id that at least isn't the literal 'user-id'
          createdBy = `tmp-${Date.now()}`;
          console.info('[CreateLobby] crypto.randomUUID not available — using fallback created_by:', createdBy);
        }
      }

      const { data, error } = await supabase
        .from('lobbies')
        .insert([
          {
            name: lobbyData.name,
            description: lobbyData.description,
            max_players: lobbyData.maxPlayers,
            created_by: createdBy
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error creating lobby:', error);
        setErrorMessage(error.message || 'Failed to create lobby');
      } else {
        setCreatedLobby(data);
      }
    } catch (err: any) {
      console.error('Unexpected error creating lobby:', err);
      setErrorMessage(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-4 sm:p-6 glass card">
      {!createdLobby ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Create New Badminton Lobby</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-600">Lobby Name</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              value={lobbyData.name}
              onChange={(e) => setLobbyData({...lobbyData, name: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              value={lobbyData.description}
              onChange={(e) => setLobbyData({...lobbyData, description: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Max Players</label>
            <input
              type="number"
              min="2"
              max="32"
              className="mt-1 block w-full rounded-xl border border-gray-200 bg-white/60 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              value={lobbyData.maxPlayers}
              onChange={(e) => setLobbyData({...lobbyData, maxPlayers: parseInt(e.target.value)})}
            />
          </div>
          {errorMessage && (
            <p className="text-red-600 text-sm">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full text-white py-2 px-4 rounded-full focus:outline-none focus:ring-2 focus:ring-accent ${loading ? 'bg-gray-300' : 'bg-accent hover:bg-accent-600'} shadow-soft-lg`}
          >
            {loading ? 'Creating…' : 'Create Lobby'}
          </button>
        </form>
      ) : (
          <div className="text-center">
          <h3 className="text-xl font-semibold mb-4 text-gray-900">Lobby Created Successfully!</h3>
          <div className="bg-white/80 p-4 rounded-lg inline-block card w-44 h-44 sm:w-56 sm:h-56">
            <div className="w-full h-full flex items-center justify-center">
              <QRCodeSVG 
                value={`${window.location.origin}/join/${createdLobby.id}`}
                size={160}
              />
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Share this QR code with players to join your lobby
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Lobby ID: {createdLobby.id}
          </p>

          <div className="mt-4">
            <button
              onClick={async () => {
                // navigate to the full Matchmaking page for this lobby
                // refresh players (optional) then navigate
                await fetchPlayers(createdLobby.id);
                const nav = navigateTo;
                nav(`/lobby/${createdLobby.id}/matchmaker`);
              }}
              className="mt-2 bg-accent text-white px-4 py-2 rounded-full shadow-soft-lg hover:bg-accent-600"
            >
              Open Matchmaker (Queue Master)
            </button>
            <button
              onClick={() => fetchPlayers(createdLobby.id)}
              className="ml-2 mt-2 bg-gray-100 text-sm px-3 py-2 rounded-full"
            >
              Refresh Players ({players.length})
            </button>
          </div>

          {/* Matchmaker is now a full page at /lobby/:lobbyId/matchmaker */}
        </div>
      )}
    </div>
  );
};

// create a navigate function once (avoid calling hook inside event handler earlier)
const CreateLobbyFormWithNavigate: React.FC = () => {
  const navigate = useNavigate();
  // expose navigate to inner handler by assigning to outer variable
  (CreateLobbyForm as any).navigate = navigate;
  return <CreateLobbyForm />;
};

// Helper to get navigate where needed
const navigateTo = (path: string) => {
  const fn = (CreateLobbyForm as any).navigate as ((p: string) => void) | undefined;
  if (fn) fn(path);
};

export default CreateLobbyFormWithNavigate;
